import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  Fade,
  IconButton,
  Link,
  Stack,
  TextField,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import FacebookIcon from '@mui/icons-material/Facebook';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import InstagramIcon from '@mui/icons-material/Instagram';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import axios from 'axios';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { baseUrl, s3ToObjectUrl } from '../constant/config';
import useRecordingPublish, { requestErrorMessage } from './useRecordingPublish';

const PLATFORMS = [
  { key: 'facebook', label: 'Facebook', sub: 'Page video', icon: FacebookIcon, color: '#1877F2' },
  { key: 'instagram', label: 'Instagram', sub: 'Reel', icon: InstagramIcon, color: '#E1306C' },
];

const CAPTION_LIMIT = 2200;

const formatDuration = (seconds) => {
  if (!seconds) return '--';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

/**
 * A selectable platform card. Reads as a button, not a form field - this modal
 * is a prompt, so the choice should be the most visible thing in it.
 */
function PlatformCard({ platform, selected, disabled, missing, onToggle }) {
  const { label, sub, icon: Icon, color } = platform;

  return (
    <Box
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-pressed={selected}
      aria-disabled={disabled}
      onClick={disabled ? undefined : onToggle}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onToggle();
        }
      }}
      sx={{
        position: 'relative',
        flex: 1,
        p: 2,
        borderRadius: 2,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        border: '2px solid',
        borderColor: selected ? color : 'divider',
        bgcolor: selected ? alpha(color, 0.08) : 'transparent',
        transition: 'border-color .15s, background-color .15s',
        '&:hover': disabled ? {} : { borderColor: selected ? color : 'text.disabled' },
        '&:focus-visible': { outline: `2px solid ${color}`, outlineOffset: 2 },
      }}
    >
      {selected && (
        <CheckCircleIcon sx={{ position: 'absolute', top: 8, right: 8, fontSize: 18, color }} />
      )}
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Icon sx={{ fontSize: 30, color: disabled ? 'text.disabled' : color }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>
            {label}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
            {disabled ? 'Not connected' : sub}
          </Typography>
        </Box>
      </Stack>
      {disabled && missing?.length > 0 && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.disabled' }}>
          Missing {missing[0]}
        </Typography>
      )}
    </Box>
  );
}

PlatformCard.propTypes = {
  platform: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
  missing: PropTypes.array,
  onToggle: PropTypes.func.isRequired,
};

/**
 * Shown once when a debate session finishes and its recording appears.
 * Asks whether to publish it, rather than leaving the admin to notice the new
 * row on their own.
 */
export default function NewRecordingShareModal({ open, video, onClose, onDismissForever }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [selected, setSelected] = useState([]);
  const [caption, setCaption] = useState('');
  const [preview, setPreview] = useState(false);
  const { publish: send, publishing, checking, results, notice, reset, busy } = useRecordingPublish(video);

  const {
    data: targets,
    isError: targetsFailed,
    error: targetsError,
    refetch: retryTargets,
    isFetching: targetsFetching,
  } = useQuery(
    ['shareTargets'],
    async () => {
      const { data } = await axios.get(`${baseUrl}/api/videos/share-targets`);
      return data.data;
    },
    { refetchOnWindowFocus: false, staleTime: 5 * 60 * 1000, enabled: open, retry: 1 }
  );

  useEffect(() => {
    if (open) {
      reset();
      setPreview(false);
      setCaption(video?.debate?.title || '');
      // Preselect whatever is actually connected - the common case is
      // publishing everywhere, and an empty selection makes the button dead.
      const ready = PLATFORMS.filter((p) => targets?.[p.key]?.configured).map((p) => p.key);
      setSelected(ready);
    }
  }, [open, video, targets, reset]);

  const publish = () => send(selected, caption);

  if (!video) return null;

  const anyConfigured = PLATFORMS.some((p) => targets?.[p.key]?.configured);
  const done = Boolean(results);
  const allPublished = done && results.every((r) => r.status === 'published');

  const toggle = (key) =>
    setSelected((prev) => (prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]));

  return (
    <Dialog
      open={open}
      onClose={publishing ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      TransitionComponent={Fade}
      transitionDuration={180}
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 3 },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: { xs: '100%', sm: 'calc(100% - 64px)' },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          flexShrink: 0,
          px: 3,
          pt: 3,
          pb: 2,
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'relative',
        }}
      >
        <IconButton
          size="small"
          onClick={onClose}
          disabled={publishing}
          sx={{ position: 'absolute', top: 12, right: 12 }}
          aria-label="Close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <FiberNewIcon color="primary" />
          <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700 }}>
            Session completed
          </Typography>
        </Stack>

        <Typography variant="h6" sx={{ lineHeight: 1.3 }}>
          {video.debate?.title || 'Untitled debate'}
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <Chip size="small" variant="outlined" label={formatDuration(video.duration)} />
          <Chip
            size="small"
            variant="outlined"
            label={`${video.layers?.length || 0} file${video.layers?.length === 1 ? '' : 's'}`}
          />
        </Stack>
      </Box>

      <Box sx={{ flex: '1 1 auto', overflowY: 'auto', minHeight: 0, px: 3, py: 2.5 }}>
        {/* Preview: poster first, load the video only if asked. */}
        <Box
          onClick={() => !preview && setPreview(true)}
          sx={{
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'grey.900',
            aspectRatio: '16 / 9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: preview ? 'default' : 'pointer',
            mb: 2.5,
          }}
        >
          {preview ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video
              src={s3ToObjectUrl(video.path)}
              controls
              autoPlay
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          ) : (
            <Stack alignItems="center" spacing={0.5} sx={{ color: 'common.white' }}>
              <PlayCircleFilledIcon sx={{ fontSize: 48, opacity: 0.9 }} />
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Preview recording
              </Typography>
            </Stack>
          )}
        </Box>

        {notice && (
          <Alert
            severity={checking ? 'warning' : 'info'}
            icon={checking ? <CircularProgress size={18} /> : undefined}
            sx={{ mb: 1.5 }}
          >
            {notice}
          </Alert>
        )}

        {done ? (
          <Stack spacing={1}>
            {results.map((r) => (
              <Alert key={r.platform} severity={r.status === 'published' ? 'success' : 'error'}>
                <strong style={{ textTransform: 'capitalize' }}>{r.platform}</strong>
                {r.status === 'published' ? (
                  <>
                    {' published.'}
                    {r.permalink && (
                      <>
                        {' '}
                        <Link href={r.permalink} target="_blank" rel="noreferrer">
                          View post
                        </Link>
                      </>
                    )}
                  </>
                ) : (
                  <>: {r.error}</>
                )}
              </Alert>
            ))}
          </Stack>
        ) : (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Share this recording to
            </Typography>

            <Stack direction="row" spacing={1.5} sx={{ mb: 2.5 }}>
              {PLATFORMS.map((p) => (
                <PlatformCard
                  key={p.key}
                  platform={p}
                  selected={selected.includes(p.key)}
                  disabled={!targets?.[p.key]?.configured || busy}
                  missing={targets?.[p.key]?.missing}
                  onToggle={() => toggle(p.key)}
                />
              ))}
            </Stack>

            {targetsFailed && !targets && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={() => retryTargets()} disabled={targetsFetching}>
                    {targetsFetching ? 'Retrying…' : 'Retry'}
                  </Button>
                }
              >
                Could not load the sharing options. {requestErrorMessage(targetsError)}
              </Alert>
            )}

            {!anyConfigured && targets && (
              <Alert severity="info" sx={{ mb: 2 }}>
                No platform is connected yet. Add the Facebook and Instagram credentials to the
                backend <code>.env</code>, then restart the API.
              </Alert>
            )}

            <TextField
              label="Caption"
              multiline
              minRows={2}
              fullWidth
              size="small"
              value={caption}
              disabled={busy}
              onChange={(e) => setCaption(e.target.value.slice(0, CAPTION_LIMIT))}
              helperText={`${caption.length} / ${CAPTION_LIMIT}`}
            />

            {publishing && (
              <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mt: 2 }}>
                Publishing. Instagram transcodes first, so this can take up to two minutes.
              </Alert>
            )}
          </>
        )}
      </Box>

      {/* Actions */}
      <Stack
        direction="row"
        spacing={1}
        justifyContent="space-between"
        alignItems="center"
        sx={{ flexShrink: 0, px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}
      >
        {done ? (
          <>
            <Box />
            <Button variant="contained" onClick={onClose}>
              {allPublished ? 'Done' : 'Close'}
            </Button>
          </>
        ) : (
          <>
            <Button size="small" color="inherit" onClick={onDismissForever} disabled={publishing}>
              Don&apos;t ask again
            </Button>
            <Stack direction="row" spacing={1}>
              <Button color="inherit" onClick={onClose} disabled={publishing}>
                Later
              </Button>
              <Button
                variant="contained"
                onClick={() => publish()}
                disabled={busy || selected.length === 0}
                startIcon={publishing ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {publishing ? 'Publishing...' : 'Share now'}
              </Button>
            </Stack>
          </>
        )}
      </Stack>
    </Dialog>
  );
}

NewRecordingShareModal.propTypes = {
  open: PropTypes.bool.isRequired,
  video: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onDismissForever: PropTypes.func.isRequired,
};
